import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CustomEvent, SetAlertType } from "schemas/functions";

import Drawer from "components/Drawer";
import { fetcherAuth } from "utils/api";
import { titleCase } from "utils/string";

import AdminLoginAs from "../Organization/LoginAs";
import BrandSearchDialog from "./BrandSearchDialog";

interface VAUserDetails {
  organization: {
    id: number;
    name: string;
    vaStatus: string;
    type: string;
    createdAt: string | null;
  };
  user: {
    id: number;
    name: string;
    email: string;
  } | null;
  integration: {
    id: number;
    provider: string;
    email: string;
    integrationError: string | null;
    refreshRequired: boolean;
    outreachPaused: boolean;
  } | null;
  subscription: {
    status: string;
    planName: string | null;
    createdAt: string | null;
  } | null;
}

interface AutomationStat {
  weekStartDate: string;
  totalBrandCount: number;
  cashBrandCount: number;
  cashOnlyBrandCount: number;
  affiliateBrandCount: number;
  productsOrServicesBrandCount: number;
  neverContactedBrandCount: number;
  hasDirectContactBrandCount: number;
  goodResponseRankingBrandCount: number;
  failedValidationBrandCount: number;
  sameCountryBrandCount: number | null;
  lowNichenessScoreBrandCount: number | null;
  mediumNichenessScoreBrandCount: number | null;
  highNichenessScoreBrandCount: number | null;
}

interface NicheTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  contentType: string;
}

interface NicheFollowUpResponse {
  question: string | null;
  selectedOptions: { option: string }[];
}

interface NicheAutomation {
  id: number;
  status: string;
  isTopNiche: boolean;
  searchParameters: Record<string, unknown> | null;
  templates: NicheTemplate[];
}

interface Niche {
  id: number;
  name: string;
  status: string;
  locationName: string | null;
  templates: NicheTemplate[];
  followUpResponses: NicheFollowUpResponse[];
  automations: NicheAutomation[];
}

const STATS_PER_PAGE = 5;

interface VAUserDrawerProps {
  onClose: () => void;
  setAlert: SetAlertType;
  categoriesById: Record<number, string>;
}

export default function VAUserDrawer({
  onClose,
  setAlert,
  categoriesById,
}: VAUserDrawerProps) {
  const { getToken } = useAuth();
  const { id } = useParams();
  const organizationId = id ? Number(id) : null;
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<VAUserDetails | null>(null);

  const [statsPage, setStatsPage] = useState(1);
  const [stats, setStats] = useState<AutomationStat[]>([]);
  const [statsTotal, setStatsTotal] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);

  const [niches, setNiches] = useState<Niche[]>([]);
  const [nichesLoading, setNichesLoading] = useState(false);
  const [expandedNicheId, setExpandedNicheId] = useState<number | null>(null);
  const [templateDialog, setTemplateDialog] = useState<NicheTemplate | null>(
    null,
  );
  const [brandSearchOpen, setBrandSearchOpen] = useState(false);

  useEffect(() => {
    if (organizationId) {
      fetchDetails(organizationId);
      setStatsPage(1);
      fetchStats(organizationId, 1);
      fetchNiches(organizationId);
    } else {
      setDetails(null);
      setStats([]);
      setStatsTotal(0);
      setNiches([]);
      setExpandedNicheId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const fetchDetails = async (orgId: number) => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${orgId}/details`,
        "GET",
      );
      setDetails(res);
    } catch (error: any) {
      setAlert(error?.message || "Failed to load user details", "warning");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (orgId: number, page: number) => {
    setStatsLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${orgId}/automation-stats?page=${page}&per_page=${STATS_PER_PAGE}`,
        "GET",
      );
      setStats(res.stats);
      setStatsTotal(res.total);
    } catch (error: any) {
      setAlert(error?.message || "Failed to load automation stats", "warning");
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchNiches = async (orgId: number) => {
    setNichesLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${orgId}/niches`,
        "GET",
      );
      setNiches(res.niches);
    } catch (error: any) {
      setAlert(error?.message || "Failed to load niches", "warning");
    } finally {
      setNichesLoading(false);
    }
  };

  const handleStatsPageChange = (_: CustomEvent, newPage: number) => {
    const page = newPage + 1;
    setStatsPage(page);
    if (organizationId) {
      fetchStats(organizationId, page);
    }
  };

  const pct = (count: number | null, total: number) => {
    if (!total || count == null) return "-";
    return `${Math.round((count / total) * 100)}%`;
  };

  const getEmailConnectionStatus = () => {
    if (!details?.integration) {
      return { label: "No Email Connected", color: "error" as const };
    }
    if (
      details.integration.integrationError ||
      details.integration.refreshRequired
    ) {
      return { label: "Disconnected", color: "error" as const };
    }
    if (details.integration.outreachPaused) {
      return { label: "Paused", color: "warning" as const };
    }
    return { label: "Connected", color: "success" as const };
  };

  const emailStatus = details ? getEmailConnectionStatus() : null;

  return (
    <Drawer
      open={!!organizationId}
      onClose={onClose}
      width={{ xs: "auto", md: "90%", lg: "80%" }}
    >
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Button onClick={onClose}>Close</Button>
          <Typography variant="h6">
            {details?.organization?.name || `Org ${organizationId}`}
          </Typography>
          <Box sx={{ width: 64 }} />
        </Grid>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Grid>
        ) : details ? (
          <Grid container direction="column" spacing={3}>
            {/* Email Connection Status */}
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                Email Connection
              </Typography>
              <Box
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Chip
                  label={emailStatus?.label}
                  color={emailStatus?.color}
                  size="small"
                />
                {details.integration?.email && (
                  <Typography variant="body2">
                    {details.integration.email}
                  </Typography>
                )}
              </Box>
              {details.integration?.integrationError && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  Error: {details.integration.integrationError}
                </Typography>
              )}
            </Grid>

            <Grid item>
              <Divider />
            </Grid>

            {/* Organization Info */}
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                Organization
              </Typography>
              <Box sx={{ mt: 1 }}>
                <InfoRow label="Org ID" value={details.organization.id} />
                <InfoRow label="Name" value={details.organization.name} />
                <InfoRow label="Type" value={details.organization.type} />
                <InfoRow
                  label="VA Status"
                  value={details.organization.vaStatus}
                />
                <InfoRow
                  label="Created"
                  value={
                    details.organization.createdAt
                      ? new Date(
                          details.organization.createdAt,
                        ).toLocaleDateString()
                      : "-"
                  }
                />
              </Box>
            </Grid>

            {/* User Info */}
            {details.user && (
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  User
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <InfoRow label="User ID" value={details.user.id} />
                  <InfoRow label="Name" value={details.user.name} />
                  <InfoRow label="Email" value={details.user.email} />
                </Box>
              </Grid>
            )}

            {/* Subscription Info */}
            {details.subscription && (
              <Grid item>
                <Typography variant="subtitle2" color="text.secondary">
                  Subscription
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <InfoRow label="Status" value={details.subscription.status} />
                  <InfoRow label="Plan" value={details.subscription.planName} />
                  <InfoRow
                    label="Since"
                    value={
                      details.subscription.createdAt
                        ? new Date(
                            details.subscription.createdAt,
                          ).toLocaleDateString()
                        : "-"
                    }
                  />
                </Box>
              </Grid>
            )}

            <Grid item>
              <Divider />
            </Grid>

            {/* Login As */}
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Actions
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <AdminLoginAs
                  organizationId={details.organization.id}
                  setAlert={setAlert}
                />
              </Box>
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => setBrandSearchOpen(true)}
                >
                  Search Brands
                </Button>
              </Box>
            </Grid>

            <Grid item>
              <Divider />
            </Grid>

            {/* Weekly Automation Stats */}
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Weekly Automation Stats
              </Typography>
              {statsLoading ? (
                <Grid container justifyContent="center" sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </Grid>
              ) : stats.length > 0 ? (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Week</TableCell>
                          <TableCell align="right">Brands</TableCell>
                          <TableCell align="right">Cash</TableCell>
                          <TableCell align="right">Affiliate</TableCell>
                          <TableCell align="right">Products</TableCell>
                          <TableCell align="right">New</TableCell>
                          <TableCell align="right">Direct</TableCell>
                          <TableCell align="right">Good Resp.</TableCell>
                          <TableCell align="right">Failed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.map((row) => (
                          <TableRow key={row.weekStartDate}>
                            <TableCell>
                              {new Date(row.weekStartDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              {row.totalBrandCount}
                            </TableCell>
                            <TableCell align="right">
                              {pct(row.cashBrandCount, row.totalBrandCount)}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.affiliateBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.productsOrServicesBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.neverContactedBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.hasDirectContactBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.goodResponseRankingBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {pct(
                                row.failedValidationBrandCount,
                                row.totalBrandCount,
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={statsTotal}
                    rowsPerPage={STATS_PER_PAGE}
                    rowsPerPageOptions={[STATS_PER_PAGE]}
                    page={statsPage - 1}
                    onPageChange={handleStatsPageChange}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No automation stats available.
                </Typography>
              )}
            </Grid>

            <Grid item>
              <Divider />
            </Grid>

            {/* Niches */}
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Niches
              </Typography>
              {nichesLoading ? (
                <Grid container justifyContent="center" sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </Grid>
              ) : niches.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {niches.map((niche) => (
                    <Box
                      key={niche.id}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      {/* Niche header — always visible */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1.5,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                        onClick={() =>
                          setExpandedNicheId(
                            expandedNicheId === niche.id ? null : niche.id,
                          )
                        }
                      >
                        <Chip label={niche.name || "Unnamed"} size="small" />
                        <Chip
                          label={niche.status}
                          size="small"
                          color={
                            niche.status === "active" ? "success" : "default"
                          }
                          variant="outlined"
                        />
                        {niche.locationName && (
                          <Typography variant="body2" color="text.secondary">
                            {niche.locationName}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: "auto" }}
                        >
                          {niche.templates.length} template
                          {niche.templates.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>

                      {/* Expanded content */}
                      <Collapse in={expandedNicheId === niche.id}>
                        <Box sx={{ px: 1.5, pb: 1.5 }}>
                          {/* Niche-level templates */}
                          {niche.templates.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Templates
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                  mt: 0.5,
                                }}
                              >
                                {niche.templates.map((t) => (
                                  <Chip
                                    key={t.id}
                                    label={t.name || t.contentType}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setTemplateDialog(t)}
                                    sx={{ cursor: "pointer" }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Follow-up responses */}
                          {niche.followUpResponses.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Follow-Up Responses
                              </Typography>
                              {niche.followUpResponses.map((resp, i) => (
                                <Box key={i} sx={{ mt: 0.5 }}>
                                  <Typography variant="body2">
                                    {resp.question}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                      mt: 0.25,
                                    }}
                                  >
                                    {resp.selectedOptions.map((opt, j) => (
                                      <Chip
                                        key={j}
                                        label={opt.option}
                                        size="small"
                                        variant="outlined"
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Automations */}
                          {niche.automations.length > 0 && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Automations
                              </Typography>
                              {niche.automations.map((auto) => (
                                <Box
                                  key={auto.id}
                                  sx={{
                                    mt: 0.5,
                                    p: 1,
                                    bgcolor: "action.hover",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 0.5,
                                    }}
                                  >
                                    <Chip
                                      label={auto.status}
                                      size="small"
                                      color={
                                        auto.status === "active"
                                          ? "success"
                                          : "default"
                                      }
                                      variant="outlined"
                                    />
                                    {auto.isTopNiche && (
                                      <Chip
                                        label="Top Niche"
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>

                                  {/* Search parameters */}
                                  {auto.searchParameters && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                        mb: 0.5,
                                      }}
                                    >
                                      {Object.entries(auto.searchParameters)
                                        .filter(
                                          ([, v]) =>
                                            v != null &&
                                            v !== "" &&
                                            !(
                                              Array.isArray(v) && v.length === 0
                                            ),
                                        )
                                        .flatMap(([key, val]) => {
                                          if (
                                            key === "category_ids" &&
                                            Array.isArray(val)
                                          ) {
                                            return val.map((id) => (
                                              <Chip
                                                key={`cat-${id}`}
                                                label={
                                                  categoriesById[Number(id)] ||
                                                  String(id)
                                                }
                                                size="small"
                                                sx={{
                                                  backgroundColor:
                                                    "primary.light",
                                                }}
                                              />
                                            ));
                                          }
                                          const display = Array.isArray(val)
                                            ? val.join(", ")
                                            : String(val);
                                          return (
                                            <Chip
                                              key={key}
                                              label={`${titleCase(
                                                key,
                                              )}: ${display}`}
                                              size="small"
                                              variant="outlined"
                                            />
                                          );
                                        })}
                                    </Box>
                                  )}

                                  {/* Automation templates */}
                                  {auto.templates.length > 0 && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                      }}
                                    >
                                      {auto.templates.map((t) => (
                                        <Chip
                                          key={t.id}
                                          label={t.name || t.contentType}
                                          size="small"
                                          variant="outlined"
                                          onClick={() => setTemplateDialog(t)}
                                          sx={{ cursor: "pointer" }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No niches available.
                </Typography>
              )}
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>

      {/* Template preview dialog */}
      <Dialog
        open={!!templateDialog}
        onClose={() => setTemplateDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        {templateDialog && (
          <>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6">
                  {templateDialog.name || "Template"}
                </Typography>
                <IconButton onClick={() => setTemplateDialog(null)}>
                  <Typography variant="body2">X</Typography>
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Subject
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {templateDialog.subject || "-"}
              </Typography>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Body
              </Typography>
              <Box
                sx={{
                  p: 1,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: "auto",
                }}
                dangerouslySetInnerHTML={{
                  __html: templateDialog.body || "-",
                }}
              />
            </DialogContent>
          </>
        )}
      </Dialog>

      {organizationId && (
        <BrandSearchDialog
          open={brandSearchOpen}
          onClose={() => setBrandSearchOpen(false)}
          organizationId={organizationId}
          niches={niches}
          setAlert={setAlert}
        />
      )}
    </Drawer>
  );
}

function InfoRow({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <Grid container spacing={1} sx={{ mb: 0.5 }}>
      <Grid item xs={4}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body2">{value ?? "-"}</Typography>
      </Grid>
    </Grid>
  );
}
